import React, { useEffect, useRef, useState } from "react";
import { mixUrl, takeChannel } from "@/lib/audioPool";
import { AnimatePresence, motion } from "framer-motion";
import { SkipForward } from "lucide-react";
import { IntroParticles } from "@/components/clientDemo/intro/IntroFx";

const GOLD = ["#F4D88A", "#D4AF37", "#C9A227", "#FFF4CC"];
const TITLE = "THE EXCHANGE ALLIANCE";
const BEATS = { medallion: 500, sweep: 1700, title: 2800, subtitle: 4300, tagline: 5600, mandate: 6900 };
const MIN_MS = 9000;
const SWEEP_EVERY = 3400;
const BURST_MS = 1500;
const MAX_MS = 30000;

function useBeat() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const ids = Object.values(BEATS).map((ms) => setTimeout(() => setT(ms), ms));
    return () => ids.forEach(clearTimeout);
  }, []);
  return (key) => t >= BEATS[key];
}

export function TypedTitle({ text = TITLE, testId = "exchange-intro-title" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((x) => (x >= text.length ? x : x + 1)), 48);
    return () => clearInterval(id);
  }, [text]);
  return (
    <h1 data-testid={testId} className="font-extrabold tracking-[0.08em] text-3xl sm:text-5xl lg:text-6xl bg-gradient-to-b from-[#FFF4CC] via-[#F4D88A] to-[#C9A227] bg-clip-text text-transparent">
      {text.slice(0, n)}
      <span className="inline-block w-[3px] h-[0.9em] align-[-0.1em] ml-1 bg-[#F4D88A] animate-pulse" style={{ opacity: n >= text.length ? 0 : 1 }} />
    </h1>
  );
}

function useSweepTick(on) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), SWEEP_EVERY);
    return () => clearInterval(id);
  }, [on]);
  return tick;
}

export function Motes() {
  const motes = useRef(Array.from({ length: 26 }, () => ({
    x: Math.random() * 100, s: 1.5 + Math.random() * 3, d: 6 + Math.random() * 7, delay: Math.random() * 6, c: GOLD[Math.floor(Math.random() * GOLD.length)],
  }))).current;
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none" data-testid="exchange-intro-motes">
      {motes.map((m, i) => (
        <motion.span key={i} className="absolute bottom-0 rounded-full"
          style={{ left: `${m.x}%`, width: m.s, height: m.s, background: m.c, boxShadow: `0 0 ${m.s * 4}px ${m.c}` }}
          initial={{ y: 0, opacity: 0 }} animate={{ y: "-100vh", opacity: [0, 0.9, 0] }}
          transition={{ duration: m.d, delay: m.delay, repeat: Infinity, ease: "linear" }} />
      ))}
    </div>
  );
}

export function Medallion({ logoUrl, sweep, innerRef, delay = BEATS.medallion }) {
  const tick = useSweepTick(sweep);
  return (
    <motion.div ref={innerRef} className="relative w-44 h-44 sm:w-56 sm:h-56"
      animate={sweep ? { scale: [1, 1.035, 1] } : undefined} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
      <motion.svg aria-hidden className="absolute -inset-9 w-[calc(100%+4.5rem)] h-[calc(100%+4.5rem)]" viewBox="0 0 100 100"
        initial={{ opacity: 0 }} animate={{ opacity: 0.7, rotate: 360 }} transition={{ opacity: { delay: 1.4, duration: 1 }, rotate: { duration: 40, repeat: Infinity, ease: "linear" } }}>
        <circle cx="50" cy="50" r="48" fill="none" stroke="#F4D88A" strokeWidth="0.4" strokeDasharray="1 3.2" />
      </motion.svg>
      <svg className="absolute -inset-4 w-[calc(100%+2rem)] h-[calc(100%+2rem)] -rotate-90" viewBox="0 0 100 100" aria-hidden>
        <motion.circle cx="50" cy="50" r="47" fill="none" stroke="#D4AF37" strokeWidth="1.2" strokeLinecap="round"
          style={{ filter: "drop-shadow(0 0 4px #F4D88A)" }}
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.3, ease: "easeInOut" }} />
      </svg>
      <motion.div aria-hidden className="absolute -inset-10 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.45), transparent 65%)" }}
        initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.7] }} transition={{ duration: 2.2 }} />
      <motion.div className="relative w-full h-full rounded-full overflow-hidden"
        initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: delay / 1000, duration: 1, ease: [0.2, 0.8, 0.2, 1] }}>
        {logoUrl && <img src={logoUrl} alt="The Exchange Alliance" data-testid="exchange-intro-medallion" className="w-full h-full object-contain" />}
        {sweep && (
          <motion.div key={tick} aria-hidden data-testid="exchange-intro-sweep" className="absolute inset-y-0 w-1/3 -skew-x-12"
            style={{ background: "linear-gradient(90deg, transparent, rgba(224,255,252,0.85), transparent)", mixBlendMode: "screen" }}
            initial={{ x: "-120%" }} animate={{ x: "320%" }} transition={{ duration: 1.1, ease: "easeInOut" }} />
        )}
      </motion.div>
    </motion.div>
  );
}

export function SunBurst({ origin }) {
  return (
    <div aria-hidden data-testid="exchange-intro-sunburst" className="absolute z-30 w-0 h-0 flex items-center justify-center pointer-events-none"
      style={{ left: origin.x, top: origin.y }}>
      <motion.div className="absolute w-[160vmax] h-[160vmax] rounded-full"
        style={{ background: "repeating-conic-gradient(from 0deg, rgba(244,216,138,0.55) 0deg 4deg, transparent 4deg 15deg)", WebkitMaskImage: "radial-gradient(circle, #000 0%, #000 25%, transparent 62%)", maskImage: "radial-gradient(circle, #000 0%, #000 25%, transparent 62%)" }}
        initial={{ scale: 0.05, opacity: 0, rotate: 0 }} animate={{ scale: 1.2, opacity: [0, 1, 1, 0.6], rotate: 50 }} transition={{ duration: BURST_MS / 1000, ease: [0.3, 0.7, 0.2, 1] }} />
      <motion.div className="absolute w-40 h-40 rounded-full"
        style={{ background: "radial-gradient(circle, #FFFDF3 0%, #F4D88A 35%, rgba(212,175,55,0.4) 60%, transparent 72%)" }}
        initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: 30, opacity: [0, 0.6, 1, 1] }} transition={{ duration: BURST_MS / 1000, ease: [0.45, 0, 0.35, 1], times: [0, 0.3, 0.7, 1] }} />
    </div>
  );
}

export default function ExchangeIntro({ logoUrl, musicUrl, narrationUrl, onClose }) {
  const at = useBeat();
  const [leaving, setLeaving] = useState(false);
  const [burst, setBurst] = useState(null);
  const medRef = useRef(null);
  const media = useRef([]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const started = Date.now();
    let narrationDone = !narrationUrl;
    let gone = false;
    const leave = () => {
      if (gone) return;
      gone = true;
      const r = medRef.current?.getBoundingClientRect();
      setBurst(r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : { x: window.innerWidth / 2, y: window.innerHeight / 2 });
      setTimeout(() => setLeaving(true), BURST_MS - 300);
      setTimeout(() => closeRef.current(), BURST_MS + 500);
    };
    const tryLeave = () => { if (narrationDone) setTimeout(leave, Math.max(0, MIN_MS - (Date.now() - started))); };
    const mh = musicUrl ? takeChannel("music", mixUrl(musicUrl, 0.55)) : null;
    const nh = narrationUrl ? takeChannel("voice", narrationUrl) : null;
    const n = nh?.el || null;
    media.current = [mh?.el, n];
    mh?.play().catch(() => {});
    let nt;
    if (n) {
      n.onended = () => { narrationDone = true; setTimeout(tryLeave, 600); };
      n.onerror = () => { narrationDone = true; tryLeave(); };
      nt = setTimeout(() => nh.play().catch(() => { narrationDone = true; tryLeave(); }), 700);
    } else tryLeave();
    const cap = setTimeout(leave, MAX_MS);
    return () => {
      clearTimeout(nt); clearTimeout(cap);
      [mh, nh].forEach((h) => h?.release());
    };
  }, [musicUrl, narrationUrl]);

  const skip = () => { media.current.forEach((el) => { try { el?.pause(); } catch {} }); onClose(); };

  return (
    <motion.div data-testid="exchange-intro" className="fixed inset-0 z-[130] bg-[#0A0612] text-white overflow-hidden flex items-center justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: leaving ? 0 : 1 }} transition={{ duration: leaving ? 0.8 : 0.7 }}>
      <div aria-hidden className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(27,67,50,0.55), transparent 60%), radial-gradient(ellipse at 50% 110%, rgba(15,118,110,0.35), transparent 55%)" }} />
      {at("tagline") && <IntroParticles count={54} colors={GOLD} />}
      {at("sweep") && <Motes />}
      {burst && <SunBurst origin={burst} />}

      <div className="relative z-10 flex flex-col items-center text-center px-6">
        <Medallion logoUrl={logoUrl} sweep={at("sweep")} innerRef={medRef} />
        <div className="mt-10 min-h-[1.2em]">{at("title") && <TypedTitle />}</div>
        <AnimatePresence>
          {at("subtitle") && (
            <motion.p key="sub" data-testid="exchange-intro-subtitle" className="mt-4 font-mono text-xs sm:text-sm uppercase text-[#F4D88A]/90"
              initial={{ opacity: 0, letterSpacing: "0.7em" }} animate={{ opacity: 1, letterSpacing: "0.34em" }} transition={{ duration: 0.9 }}>
              PACT Life Pass Platform
            </motion.p>
          )}
        </AnimatePresence>
        <div className="mt-10 h-px w-40 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" style={{ opacity: at("tagline") ? 1 : 0, transition: "opacity .6s" }} />
        {at("tagline") && (
          <motion.p data-testid="exchange-intro-tagline" className="mt-6 text-lg sm:text-2xl lg:text-3xl font-light italic text-[#FAFAF7]"
            initial={{ opacity: 0, y: 14, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ duration: 0.8 }}>
            <motion.span className="bg-gradient-to-r from-[#FAFAF7] via-[#F4D88A] to-[#FAFAF7] bg-clip-text text-transparent bg-[length:200%_100%]"
              animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }}>
              Serving the Last, the Least and the Lost
            </motion.span>
          </motion.p>
        )}
        {at("mandate") && (
          <motion.p data-testid="exchange-intro-mandate" className="mt-3 font-mono text-[11px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37]"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}>
            His Mission · His Mandate
          </motion.p>
        )}
      </div>

      <button type="button" onClick={skip} data-testid="exchange-intro-skip"
        className="absolute top-5 right-5 z-20 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-[#D4AF37]/30 text-[#F4D88A] hover:bg-[#D4AF37]/15 font-mono text-[10px] tracking-[0.16em] uppercase transition-colors">
        Skip <SkipForward className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}
